import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './EmergencyRouteUpload.css';
import { TEXT_EMERGENCY_ROUTE_UPLOAD, TEXT_UPLOAD, TEXT_DRAG_DROP_CSV } from 'constants/message';

const propTypes = {
    onUpload: PropTypes.func.isRequired
};

const defaultProps = {};

const EmergencyRouteUpload = (props) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFileSelect(files[0]);
        }
    };

    const validateCSVContent = (text) => {
        const lines = text.split(/\r?\n/).filter(line => line.trim());

        // Check if file is empty (no header + data)
        if (lines.length < 2) {
            return { valid: false, error: 'データ形式が不正です。' };
        }

        // Check header existence and format (ヘッダがない)
        const header = lines[0];

        // Check delimiter must be semicolon (デリミタがカンマではない)
        if (!header.includes(';')) {
            return { valid: false, error: 'データ形式が不正です。' };
        }

        const delimiter = ';';
        const headerColumns = header.split(delimiter).map(col => col.trim());

        // Validate header has 4 columns
        if (headerColumns.length !== 4) {
            return { valid: false, error: 'データ形式が不正です。' };
        }

        // Validate header column names
        if (headerColumns[0] !== 'RouteName' || headerColumns[1] !== 'RoadID') {
            return { valid: false, error: 'データ形式が不正です。' };
        }

        // Validate all data rows have exactly 4 columns (データ行の項目数が異なる)
        const dataLines = lines.slice(1);

        for (let i = 0; i < dataLines.length; i++) {
            const line = dataLines[i];
            const columns = line.split(delimiter).map(col => col.trim());

            // Check exact column count
            if (columns.length !== 4) {
                return { valid: false, error: 'データ形式が不正です。' };
            }

            const [routeName, sauid, x, y] = columns;

            // Validate route name is not empty
            if (!routeName) {
                return { valid: false, error: 'データ形式が不正です。' };
            }

            // Validate SAUID is numeric
            if (!sauid || isNaN(sauid)) {
                return { valid: false, error: 'データ形式が不正です。' };
            }

            // Validate X (longitude) is numeric and in valid range
            const longitude = parseFloat(x);
            if (isNaN(longitude) || longitude < -180 || longitude > 180) {
                return { valid: false, error: 'データ形式が不正です。' };
            }

            // Validate Y (latitude) is numeric and in valid range
            const latitude = parseFloat(y);
            if (isNaN(latitude) || latitude < -89.85 || latitude > 89.85) {
                return { valid: false, error: 'データ形式が不正です。' };
            }
        }

        return { valid: true };
    };

    const handleFileSelect = async (file) => {
        if (!file) return;

        // Check file extension
        if (!file.name.endsWith('.csv')) {
            alert('CSVしか選択できない');
            return;
        }

        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('データ形式が不正です。');
            return;
        }

        // Validate CSV content
        try {
            const text = await file.text();
            const validation = validateCSVContent(text);

            if (!validation.valid) {
                alert(validation.error);
                return;
            }

            setSelectedFile(file);
        } catch (error) {
            alert('データ形式が不正です。');
            console.error('File validation error:', error);
        }
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleUploadClick = async () => {
        if (selectedFile) {
            await props.onUpload(selectedFile);
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="emergency_route_upload_container">
            <div className="emergency_route_upload_header">
                <label className="emergency_route_upload_title">{TEXT_EMERGENCY_ROUTE_UPLOAD}</label>
                <span className="emergency_route_upload_required">*</span>
            </div>

            <div
                className={`emergency_route_dropzone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
            >
                <div className="emergency_route_dropzone_icon">📁</div>
                <div className="emergency_route_dropzone_text">
                    {selectedFile ? selectedFile.name : TEXT_DRAG_DROP_CSV}
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                />
            </div>

            <button
                className="emergency_route_upload_button button_active"
                onClick={handleUploadClick}
                disabled={!selectedFile}
            >
                {TEXT_UPLOAD}
            </button>
        </div>
    );
};

EmergencyRouteUpload.propTypes = propTypes;
EmergencyRouteUpload.defaultProps = defaultProps;
export default EmergencyRouteUpload;
